// @flow

import * as React from 'react';
import {useIntl, defineMessages} from 'react-intl';
import {UnstyledLink} from 'sail/UnstyledLink';
import Box from 'sail/Box';
import Group from 'sail/Group';
import {AlignBox} from 'sail/Align';
import {Text, BodyAlt, BodyTextProps} from 'sail/Text';

import './Breadcrumbs.scss';
import {useNavigation, useLocation, useAnalytics} from 'docs/data';
import {DocsIcon} from 'docs/components';
import {
  getProductOverviewPageRoute,
  getProductDisplayName,
  getProductBreadcrumbs,
} from 'docs/util';

const messages = defineMessages({
  home: {
    id: 'docsShell.breadcrumbs.home',
    description: 'Home breadcrumb label',
    defaultMessage: 'Home',
  },
});

const HIDE_PRODUCT_BADGE = [
  'account',
  'developertools',
  'security',
  'tax_product',
  'orders',
];

type ProductCrumbProps = {|
  // TODO change type to `typeof ProductNames`
  product: string | null,
|};

const ProductCrumb = ({product}: ProductCrumbProps) => {
  if (!product || HIDE_PRODUCT_BADGE.includes(product)) {
    return null;
  }
  const overviewPage = getProductOverviewPageRoute(product);
  const productDisplayName = getProductDisplayName(product);
  return (
    <Group direction="horizontal" spacing={4} className="Breadcrumbs-product">
      <UnstyledLink href={overviewPage}>
        <AlignBox reference={BodyTextProps} style={{cursor: 'pointer'}}>
          <DocsIcon category="product" icon={product} size={16} />
          <Box style={{paddingLeft: '6px'}}>
            <BodyAlt style={{textTransform: 'capitalize'}}>
              {productDisplayName}
            </BodyAlt>
          </Box>
        </AlignBox>
      </UnstyledLink>
      <BodyAlt>&#183;</BodyAlt>
    </Group>
  );
};

export const Breadcrumbs = () => {
  const {navigation} = useNavigation();
  const {trackAction} = useAnalytics();
  const {location} = useLocation();
  const intl = useIntl();
  const activeProduct = navigation.products.find((p) => p.active);
  let crumbsData = [{text: intl.formatMessage(messages.home), href: '/docs'}];

  if (!activeProduct) {
    return null;
  }

  // Every product should have an associated "topic". For example Radar has the topic of 'Business Operations'.
  const activeTopic = activeProduct.topic;
  const productBreadcrumbs = getProductBreadcrumbs(activeProduct);
  if (activeProduct.text === activeTopic.text) {
    // We don't want a situation where the breadcrumbs shows the same text twice because
    // the product and topic are the same name (For example this happens right now with Payments).
    // If this happens, display the topic instead of the product name in the breadcrumbs.
    productBreadcrumbs.shift();
  }

  crumbsData = crumbsData.concat([activeProduct.topic, ...productBreadcrumbs]);

  if (activeTopic.text && activeTopic.text.toLowerCase() === 'home') {
    // Some internal pages like the Style Guide have their topic set as 'Home'. If
    // this happens, we remove a crumb to prevent "Home / Home" from showing.
    crumbsData.shift();
  }

  // The last crumb will be the name of the current page. Since this will be shown
  // in the title of the content right below the breadcrumbs, remove it.
  const currentPage = crumbsData.pop();

  // Do not show crumbs that are self referencing
  crumbsData = crumbsData.filter(({href}) => location.pathname !== href);

  // Dont render any breadcrumbs on pages with less than 2 crumbs, (like docs/business)
  if (crumbsData.length < 2) {
    return null;
  }

  const crumbs = crumbsData.map(({href, text}, index) => {
    return (
      <Text
        className="Breadcrumbs-link"
        color="gray"
        weight="regular"
        key={index}
        lineHeight={20}
      >
        <UnstyledLink
          title={text}
          href={href}
          onClick={() => {
            trackAction('docs_breadcrumb_clicked', {
              link: href,
              currentPage: currentPage.href,
            });
          }}
        >
          {text}
        </UnstyledLink>
      </Text>
    );
  });

  return (
    <Group
      spacing={4}
      direction="horizontal"
      padding={{bottom: 8}}
      className="Breadcrumbs-container"
    >
      {crumbsData.length > 2 && <ProductCrumb product={activeProduct.icon} />}
      <Box className="Breadcrumbs">{crumbs}</Box>
    </Group>
  );
};
